import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_URL, formatApiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

export default function AdminPhotosCard({ t }) {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [busyIndex, setBusyIndex] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setLoadError(null);
    api.get("/admin/photos")
      .then((r) => setData(r.data))
      .catch((e) => setLoadError(formatApiError(e)));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (index) => {
    setBusyIndex(index);
    try {
      await api.post(`/admin/pixels/${index}/approve`);
      toast.success(t("adminPhotos.approvedOk"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyIndex(null);
    }
  };

  const remove = async (index) => {
    if (!window.confirm(t("adminPhotos.confirm"))) return;
    setBusyIndex(index);
    try {
      await api.delete(`/admin/pixels/${index}/photo`);
      toast.success(t("adminPhotos.removed"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyIndex(null);
    }
  };

  return (
    <div className="px-5 md:px-12 pb-16" data-testid="admin-photos-card">
      <div className="border border-gold/50 p-8">
        <div className="text-[10px] tracking-[0.3em] text-golddark">ADMIN</div>
        <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">{t("adminPhotos.title")}</h3>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("adminPhotos.sub")} — <span className="font-mono text-golddark" data-testid="admin-photos-total">{data ? data.total : "…"}</span> {lang === "it" ? "foto" : "photos"}
        </p>
        {loadError ? (
          <div className="mt-6 text-sm text-[#b04a3a]" role="alert">
            <p>{lang === "it" ? "Impossibile caricare le foto: " : "Unable to load photos: "}{loadError}</p>
            <button onClick={load} className="mt-3 px-4 py-2 border border-border">
              {lang === "it" ? "Riprova" : "Retry"}
            </button>
          </div>
        ) : data === null ? (
          <p className="mt-6 text-xs text-muted-foreground">{lang === "it" ? "Caricamento foto…" : "Loading photos…"}</p>
        ) : data.photos.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="admin-photos-grid">
            {data.photos.map((p) => (
              <div key={p.index} className="border border-border/60 p-2" data-testid={`admin-photo-${p.index}`}>
                <img
                  src={`${API_URL}/files/${p.photo_path}`}
                  alt={`${t("adminPhotos.photoOf")} ${p.user_name}`}
                  className="w-full aspect-square object-cover"
                />
                <div className="mt-2 text-[10px] font-mono text-[#3C3735]">
                  [{p.x}, {p.y}]
                </div>
                <div className="text-[10px] text-muted-foreground truncate">{p.user_name}</div>
                <div
                  className={`mt-1 text-[9px] tracking-[0.2em] ${p.approved ? "text-[#7D8C77]" : "text-golddark"}`}
                  data-testid={`admin-photo-status-${p.index}`}
                >
                  {(p.approved ? t("adminPhotos.approved") : t("adminPhotos.pending")).toUpperCase()}
                </div>
                {!p.approved && (
                  <button
                    onClick={() => approve(p.index)}
                    disabled={busyIndex === p.index}
                    data-testid={`admin-photo-approve-${p.index}`}
                    className="mt-2 w-full px-3 py-2 bg-[#7D8C77] text-[#FAF8F5] text-[9px] tracking-[0.2em] font-semibold hover:bg-[#3C3735] disabled:opacity-40 transition-colors duration-200"
                  >
                    {t("adminPhotos.approve").toUpperCase()}
                  </button>
                )}
                <button
                  onClick={() => remove(p.index)}
                  disabled={busyIndex === p.index}
                  data-testid={`admin-photo-remove-${p.index}`}
                  className="mt-2 w-full px-3 py-2 border border-[#b04a3a]/50 text-[#b04a3a] text-[9px] tracking-[0.2em] hover:bg-[#b04a3a] hover:text-[#FAF8F5] disabled:opacity-40 transition-colors duration-200"
                >
                  {t("adminPhotos.remove").toUpperCase()}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-6 text-xs text-muted-foreground" data-testid="admin-photos-empty">
            {t("adminPhotos.empty")}
          </p>
        )}
      </div>
    </div>
  );
}
